import { supabase } from './supabaseClient';
import type {
  Pilot,
  Aircraft,
  TechlogEntry,
  Sector,
  TechlogEntryWithRelations,
  AircraftWithStats,
  PilotInsert,
  AircraftInsert,
  TechlogEntryInsert,
  SectorInsert,
} from '../types/database';

// =====================================================
// PILOT QUERIES
// =====================================================

export const getPilots = async (): Promise<Pilot[]> => {
  const { data, error } = await supabase
    .from('pilots')
    .select('*')
    .eq('active', true)
    .order('name');

  if (error) throw error;
  return data || [];
};

export const getPilotByName = async (name: string): Promise<Pilot | null> => {
  const { data, error } = await supabase
    .from('pilots')
    .select('*')
    .eq('name', name)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const createPilot = async (pilot: PilotInsert): Promise<Pilot> => {
  const { data, error } = await supabase
    .from('pilots')
    .insert(pilot as any)
    .select()
    .single();

  if (error) throw error;
  return data as Pilot;
};

export const getOrCreatePilot = async (name: string): Promise<Pilot> => {
  const existing = await getPilotByName(name);
  if (existing) return existing;

  return createPilot({ name });
};

export const updatePilot = async (
  id: string,
  updates: Partial<PilotInsert>
): Promise<Pilot> => {
  const { data, error } = await (supabase as any)
    .from('pilots')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Pilot;
};

// =====================================================
// AIRCRAFT QUERIES
// =====================================================

export const getAircraft = async (): Promise<Aircraft[]> => {
  const { data, error } = await supabase
    .from('aircraft')
    .select('*')
    .eq('active', true)
    .order('registration');

  if (error) throw error;
  return data || [];
};

export const getAircraftByRegistration = async (
  registration: string
): Promise<Aircraft | null> => {
  const { data, error } = await supabase
    .from('aircraft')
    .select('*')
    .eq('registration', registration)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const createAircraft = async (
  aircraft: AircraftInsert
): Promise<Aircraft> => {
  const { data, error } = await supabase
    .from('aircraft')
    .insert(aircraft as any)
    .select()
    .single();

  if (error) throw error;
  return data as Aircraft;
};

export const getOrCreateAircraft = async (
  registration: string
): Promise<Aircraft> => {
  const existing = await getAircraftByRegistration(registration);
  if (existing) return existing;

  return createAircraft({ registration });
};

export const updateAircraft = async (
  id: string,
  updates: Partial<AircraftInsert>
): Promise<Aircraft> => {
  const { data, error } = await (supabase as any)
    .from('aircraft')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Aircraft;
};

export const getAircraftWithStats = async (): Promise<AircraftWithStats[]> => {
  const { data, error } = await supabase
    .from('aircraft')
    .select(
      `
      *,
      statistics:aircraft_statistics(*)
    `
    )
    .eq('active', true)
    .order('registration');

  if (error) throw error;
  return (data || []).map((item: any) => ({
    ...item,
    statistics: item.statistics?.[0] || null,
  }));
};

// =====================================================
// TECHLOG ENTRY QUERIES
// =====================================================

export const getTechlogEntries = async (): Promise<
  TechlogEntryWithRelations[]
> => {
  const { data, error } = await supabase
    .from('techlog_entries')
    .select(
      `
      *,
      aircraft(*),
      pilot:pilots!pilot_id(*),
      copilot:pilots!copilot_id(*),
      sectors(*)
    `
    )
    .order('date', { ascending: false });

  if (error) throw error;
  return (data || []).map((item: any) => ({
    ...item,
    pilot: item.pilot || undefined,
    copilot: item.copilot || undefined,
    aircraft: item.aircraft || undefined,
    sectors: (item.sectors || []).sort(
      (a: Sector, b: Sector) => a.sector_order - b.sector_order
    ),
  }));
};

export const getTechlogEntryById = async (
  id: string
): Promise<TechlogEntryWithRelations | null> => {
  const { data, error } = await supabase
    .from('techlog_entries')
    .select(
      `
      *,
      aircraft(*),
      pilot:pilots!pilot_id(*),
      copilot:pilots!copilot_id(*),
      sectors(*)
    `
    )
    .eq('id', id)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  
  if (!data) return null;

  const result: any = data;
  return {
    ...result,
    pilot: result.pilot || undefined,
    copilot: result.copilot || undefined,
    aircraft: result.aircraft || undefined,
    sectors: (result.sectors || []).sort(
      (a: Sector, b: Sector) => a.sector_order - b.sector_order
    ),
  };
};

export const getTechlogEntriesByPilot = async (
  pilotId: string
): Promise<TechlogEntryWithRelations[]> => {
  const { data, error } = await supabase
    .from('techlog_entries')
    .select(
      `
      *,
      aircraft(*),
      pilot:pilots!pilot_id(*),
      copilot:pilots!copilot_id(*),
      sectors(*)
    `
    )
    .or(`pilot_id.eq.${pilotId},copilot_id.eq.${pilotId}`)
    .order('date', { ascending: false });

  if (error) throw error;
  return (data || []).map((item: any) => ({
    ...item,
    pilot: item.pilot || undefined,
    copilot: item.copilot || undefined,
    aircraft: item.aircraft || undefined,
    sectors: (item.sectors || []).sort(
      (a: Sector, b: Sector) => a.sector_order - b.sector_order
    ),
  }));
};

export const createTechlogEntry = async (
  entry: TechlogEntryInsert,
  sectors: any[]
): Promise<TechlogEntryWithRelations> => {
  // First, create the techlog entry
  const { data: techlogData, error: techlogError } = await supabase
    .from('techlog_entries')
    .insert(entry as any)
    .select()
    .single();

  if (techlogError) throw techlogError;

  const techlogResult: any = techlogData;

  // Then, create all sectors
  const sectorsWithTechlogId = sectors.map((sector) => ({
    ...sector,
    techlog_entry_id: techlogResult.id,
  }));

  const { data: sectorsData, error: sectorsError } = await supabase
    .from('sectors')
    .insert(sectorsWithTechlogId as any)
    .select();

  if (sectorsError) throw sectorsError;

  // Return the complete entry with relations
  const result = await getTechlogEntryById(techlogResult.id);
  return result as TechlogEntryWithRelations;
};

export const deleteTechlogEntry = async (id: string): Promise<void> => {
  const { error } = await supabase.from('techlog_entries').delete().eq('id', id);

  if (error) throw error;
};

// =====================================================
// SECTOR QUERIES
// =====================================================

export const getSectorsByTechlogEntry = async (
  techlogEntryId: string
): Promise<Sector[]> => {
  const { data, error } = await supabase
    .from('sectors')
    .select('*')
    .eq('techlog_entry_id', techlogEntryId)
    .order('sector_order');

  if (error) throw error;
  return data || [];
};

// =====================================================
// AIRCRAFT STATISTICS QUERIES
// =====================================================

export const getAircraftStatistics = async (
  aircraftId: string
): Promise<any> => {
  const { data, error } = await supabase
    .from('aircraft_statistics')
    .select('*')
    .eq('aircraft_id', aircraftId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

// =====================================================
// UTILITY QUERIES
// =====================================================

export const clearAllData = async (): Promise<void> => {
  // Delete in order due to foreign key constraints
  await supabase.from('sectors').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  await supabase.from('techlog_entries').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  await supabase.from('aircraft_statistics').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  await supabase.from('aircraft').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  await supabase.from('pilots').delete().neq('id', '00000000-0000-0000-0000-000000000000');
};

// =====================================================
// HELPER FUNCTIONS FOR PILOT STATS CALCULATIONS
// =====================================================

export const getPilotEntriesInDateRange = async (
  pilotName: string,
  startDate: Date,
  endDate: Date
): Promise<TechlogEntryWithRelations[]> => {
  // First get the pilot
  const pilot = await getPilotByName(pilotName);
  if (!pilot) return [];

  const { data, error } = await supabase
    .from('techlog_entries')
    .select(
      `
      *,
      aircraft(*),
      pilot:pilots!pilot_id(*),
      copilot:pilots!copilot_id(*),
      sectors(*)
    `
    )
    .or(`pilot_id.eq.${pilot.id},copilot_id.eq.${pilot.id}`)
    .gte('date', startDate.toISOString().split('T')[0])
    .lte('date', endDate.toISOString().split('T')[0])
    .order('date', { ascending: false });

  if (error) throw error;
  return (data || []).map((item: any) => ({
    ...item,
    pilot: item.pilot || undefined,
    copilot: item.copilot || undefined,
    aircraft: item.aircraft || undefined,
    sectors: (item.sectors || []).sort(
      (a: Sector, b: Sector) => a.sector_order - b.sector_order
    ),
  }));
};

export const updateTechlogEntry = async (
  id: string,
  entry: Partial<TechlogEntryInsert>,
  sectors?: any[]
): Promise<TechlogEntryWithRelations> => {
  // Update the techlog entry
  const { data: techlogData, error: techlogError } = await (supabase as any)
    .from('techlog_entries')
    .update(entry)
    .eq('id', id)
    .select()
    .single();

  if (techlogError) throw techlogError;

  // If sectors are provided, delete old ones and insert new ones
  if (sectors) {
    // Delete existing sectors
    await supabase.from('sectors').delete().eq('techlog_entry_id', id);

    // Insert new sectors
    const sectorsWithTechlogId = sectors.map((sector) => ({
      ...sector,
      techlog_entry_id: id,
    }));

    const { error: sectorsError } = await (supabase as any)
      .from('sectors')
      .insert(sectorsWithTechlogId)
      .select();

    if (sectorsError) throw sectorsError;
  }

  // Return the complete updated entry
  const result = await getTechlogEntryById(id);
  return result as TechlogEntryWithRelations;
};

export const getAllUniquePilotNames = async (): Promise<string[]> => {
  const pilots = await getPilots();
  return pilots.map((p) => p.name).sort();
};

export const getAllUniqueAircraftRegistrations = async (): Promise<string[]> => {
  const aircraft = await getAircraft();
  return aircraft.map((a) => a.registration).sort();
};

